import { useState, useEffect, useContext, useCallback } from "react";

import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { AlertTriangle, Wallet } from "lucide-react";
import toast from "react-hot-toast";

import { AuthContext } from "../../context/auth-context";
import { useHttpClient } from "../../hooks/http-hook";
import { useLoading } from "../../context/LoadingContext";
import "./ModalCorrigirCaixa.scss";

/* Quem pode corrigir. O servidor verifica o mesmo — isto é só para não mostrar
   um botão que ia dar erro. */
export const PAPEIS_CORRECAO = [
  "Gerente",
  "Médico/Administrador",
  "Secretária(o)",
];

export const podeCorrigirCaixa = (auth) =>
  PAPEIS_CORRECAO.includes(auth?.role);

const formatarCVE = (valor) =>
  new Intl.NumberFormat("pt-PT", {
    style: "currency",
    currency: "CVE",
  }).format(valor || 0);

/**
 * Corrige a caixa de um pagamento já recebido ou pago — e, enquanto não houver
 * a observação, que é interna, e — enquanto não houver documento nenhum — a
 * descrição, que sai impressa.
 *
 * Valor, data, método e cliente não se corrigem aqui: saem nos documentos que
 * o cliente tem na mão. Para esses, cancela-se o recebimento e refaz-se.
 *
 * `tipo`: "parcela" | "entrada" | "despesa".
 */
function ModalCorrigirCaixa({ open, tipo, id, onFechar, onCorrigido }) {
  const auth = useContext(AuthContext);
  const { sendRequest } = useHttpClient();
  const { startLoading, stopLoading } = useLoading();

  const [dados, setDados] = useState(null);
  const [caixaEscolhida, setCaixaEscolhida] = useState("");
  /* Dois textos, com regras diferentes: a descrição sai impressa (recibo,
     documento eletrónico, ordem de pagamento) e só muda enquanto não houver
     documento; a observação é interna e muda sempre. */
  const [descricao, setDescricao] = useState("");
  const [observacao, setObservacao] = useState("");
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState("");
  const [aGuardar, setAGuardar] = useState(false);

  const carregar = useCallback(async () => {
    startLoading();
    try {
      const resposta = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/correcoesfinanceiras/caixa/${tipo}/${id}`,
        "GET",
        null,
        { Authorization: "Bearer " + auth.token },
      );
      setDados(resposta);
      setCaixaEscolhida(resposta.registo.caixa?.id || "");
      setDescricao(resposta.registo.descricao || "");
      setObservacao(resposta.registo.observacao || "");
    } catch (err) {
      setErro(err?.message || "Não foi possível carregar o registo.");
    } finally {
      stopLoading();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo, id]);

  useEffect(() => {
    if (!open) return;
    /* Sem registo não há nada a carregar. Dizer isto é melhor do que ficar
       eternamente em «A carregar…», que foi o que aconteceu quando o ecrã que
       abre esta janela não passava o identificador. */
    if (!tipo || !id) {
      setErro("Não foi possível identificar o registo a corrigir.");
      return;
    }
    carregar();
  }, [open, tipo, id, carregar]);

  const guardar = async () => {
    setErro("");

    if (motivo.trim().length < 5) {
      setErro(
        "Escreva o motivo da correção com pelo menos 5 letras — fica no histórico da clínica.",
      );
      return;
    }

    const mudouCaixa = caixaEscolhida !== (dados.registo.caixa?.id || "");
    const mudouTexto =
      descricao !== (dados.registo.descricao || "") ||
      observacao !== (dados.registo.observacao || "");

    if (!mudouCaixa && !mudouTexto) {
      setErro("Não mudou nada.");
      return;
    }

    setAGuardar(true);
    startLoading();
    try {
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/correcoesfinanceiras/caixa/${tipo}/${id}`,
        "PATCH",
        JSON.stringify({
          caixa: caixaEscolhida,
          descricao: dados.podeEditarDescricao ? descricao : undefined,
          observacao,
          motivo: motivo.trim(),
          utilizador: auth.userId,
        }),
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );
      toast.success("Correção registada.");
      onCorrigido?.();
      onFechar();
    } catch (err) {
      setErro(err?.message || "Não foi possível guardar a correção.");
    } finally {
      setAGuardar(false);
      stopLoading();
    }
  };

  const caixaAtualId = dados?.registo.caixa?.id || "";
  const destino = dados?.caixas.find((c) => c.id === caixaEscolhida);
  const origem = dados?.caixas.find((c) => c.id === caixaAtualId);
  const valor = dados?.registo.valor || 0;
  const mudouCaixa = !!dados && caixaEscolhida !== caixaAtualId;

  /* A única caixa que o método permite é a que já lá está. */
  const permitidas = dados?.caixas.filter((c) => !c.recusadaPelaRegra) ?? [];
  const semAlternativaDeCaixa =
    !!dados &&
    permitidas.length === 1 &&
    permitidas[0].id === caixaAtualId &&
    !!dados.registo.metodoPagamento;

  return (
    <Modal open={open} onClose={onFechar}>
      <Box className="fluxo-atendimento__modal__box corrigir-caixa">
        <h1 className="fluxo-atendimento__modal__titulo">Editar</h1>

        {!dados && !erro && <p className="corrigir-caixa__info">A carregar…</p>}

        {erro && (
          <p className="corrigir-caixa__erro">
            <AlertTriangle size={16} /> {erro}
          </p>
        )}

        {dados && (
          <>
            <p className="corrigir-caixa__info">
              {formatarCVE(valor)} ·{" "}
              {dados.registo.metodoPagamento || "sem método"} ·{" "}
              {dados.registo.dataPagamento
                ? new Date(dados.registo.dataPagamento).toLocaleDateString(
                    "pt-PT",
                  )
                : "sem data"}
              {/* A caixa atual escrita por extenso: o seletor mostra-a, mas
                  depois de se mexer nele já não se sabe qual era. */}
              <br />
              Caixa atual: <strong>{dados.registo.caixa?.nome || "—"}</strong>
            </p>

            <label className="corrigir-caixa__etiqueta" htmlFor="caixaDestino">
              Caixa
            </label>
            <select
              id="caixaDestino"
              className="corrigir-caixa__campo"
              value={caixaEscolhida}
              onChange={(e) => setCaixaEscolhida(e.target.value)}
            >
              {dados.caixas.map((c) => (
                <option key={c.id} value={c.id} disabled={c.recusadaPelaRegra}>
                  {c.nome}
                  {/* O saldo só vem a quem o pode ver. Para os restantes, a
                      lista é só de nomes. */}
                  {dados.mostrarSaldos && c.saldo !== null
                    ? ` · ${formatarCVE(c.saldo)}`
                    : ""}
                  {c.recusadaPelaRegra ? " (não permitida para este método)" : ""}
                </option>
              ))}
            </select>

            {/* Numa clínica com duas caixas, o método muitas vezes só permite
                a caixa que o registo já tem — não há para onde mudar. Dizê-lo
                evita a pessoa procurar uma opção que não existe e levar com um
                «não mudou nada» ao gravar. */}
            {semAlternativaDeCaixa && (
              <p className="corrigir-caixa__nota">
                Com «{dados.registo.metodoPagamento}», só a caixa «
                {origem?.nome}» é possível: a caixa não pode mudar. Pode na
                mesma corrigir a descrição e a observação.
              </p>
            )}

            {/* O efeito é imediato e retroativo: quem corrige tem de o ver
                antes de confirmar, não depois. */}
            {mudouCaixa && origem && destino && (
              <div className="corrigir-caixa__efeito">
                <Wallet size={16} />
                <span>
                  {origem.nome}
                  {dados.mostrarSaldos && origem.saldo !== null
                    ? ` ${formatarCVE(origem.saldo)} → ${formatarCVE(origem.saldo - valor)}`
                    : ""}
                  {" → "}
                  {destino.nome}
                  {dados.mostrarSaldos && destino.saldo !== null
                    ? ` ${formatarCVE(destino.saldo)} → ${formatarCVE(destino.saldo + valor)}`
                    : ""}
                </span>
              </div>
            )}

            <label className="corrigir-caixa__etiqueta" htmlFor="descricaoCorrecao">
              Descrição
            </label>
            <input
              id="descricaoCorrecao"
              className="corrigir-caixa__campo"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              disabled={!dados.podeEditarDescricao}
            />
            {!dados.podeEditarDescricao && (
              <p className="corrigir-caixa__nota">
                A descrição já saiu impressa em {dados.documentos.join(" e ")},
                por isso não pode mudar. A observação é interna e pode.
              </p>
            )}

            <label
              className="corrigir-caixa__etiqueta"
              htmlFor="observacaoCorrecao"
            >
              Observação
            </label>
            <textarea
              id="observacaoCorrecao"
              className="corrigir-caixa__campo corrigir-caixa__campo--area"
              rows={2}
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
            />

            <label className="corrigir-caixa__etiqueta" htmlFor="motivoCorrecao">
              Motivo da correção
            </label>
            <textarea
              id="motivoCorrecao"
              className="corrigir-caixa__campo corrigir-caixa__campo--area"
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ex.: recebido em transferência, caixa escolhida por engano"
            />

            <p className="corrigir-caixa__nota">
              O valor, a data, o método e o cliente não se corrigem aqui: saem
              nos documentos. Para esses, cancele o recebimento e refaça.
            </p>

            <div className="corrigir-caixa__botoes">
              <span
                className="cancel-btn"
                onClick={onFechar}
                style={{ cursor: "pointer" }}
              >
                Cancelar
              </span>
              <span
                className="blue-button"
                onClick={aGuardar ? undefined : guardar}
                style={{ cursor: aGuardar ? "default" : "pointer" }}
              >
                {aGuardar ? "A guardar…" : "Guardar correção"}
              </span>
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
}

export default ModalCorrigirCaixa;
